import clevertap from 'clevertap-web-sdk';
import { useFlags } from 'launchdarkly-react-client-sdk';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { captureException, withScope, configureScope } from '@sentry/browser';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import i18n from 'src/config/i18n';
import { languages } from 'src/config/i18n.config.mjs';
import { navigationService } from 'src/config/rootService';
import { Dispatch, RootState } from 'src/config/rootStore';
import { useBypassAssessmentsMutation } from 'src/modules/onboarding/documents/bypassAssessmentsMutation.generated';
import {
    isOnboardedInfoFetched,
    isOnboardingCompleted,
    isOnboardingFirstStepCompleted,
    isOnboardingSecondStepCompleted,
} from 'src/modules/onboarding/selectors/onboardingSelectors';
import { isUserProvidersFetchCompleted } from 'src/modules/providers/selectors/providersSelectors';
import {
    getMostRecentCareRecommendation,
    getUserInformation,
    isUserDataFetched,
} from 'src/modules/user/selectors/userSelectors';
import { GetLastInvoicedCoachingVisitDocument } from 'src/modules/videoSessions/documents/getLastInvoicedCoachingVisit.generated';
import {
    isSessionFetchingCompleted,
    shouldShowPreSessionAssessments,
} from 'src/modules/videoSessions/selectors/videoSessionsSelectors';

import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { analyticsClient } from 'src/shared/services/analytics';

import { isAuthenticated } from '../selectors/authenticationSelectors';
import { useStartCareRecommendationMutation } from 'src/modules/onboarding/documents/startCareRecommendationMutation.generated';
import { useShouldTakeToRequestPersonalEmailAddressFlowRequiredOnly } from 'src/modules/user/hooks/userHooks';
import { isSSOFlowRegistrationSource } from 'src/shared/helpers/isSSOFlowRegistrationSource';

// accessing any of those pages with onboarding not finished will trigger onboarding bypass logic
// which will allow user to see requested content first and then to continue the onboarding from the for you action
const urlsThatSkipsOnboarding = [
    '/pause/',
    '/resources',
    '/meditations',
    '/programs',
    '/program/',
    '/meditation/',
    '/course/',
];

type Props = ReturnType<typeof mapStateToProps>;

const PostAuth: React.FC<Props> = props => {
    const flags = useFlags();

    const dispatch = useDispatch<Dispatch>();
    const careRecommendation = useSelector(getMostRecentCareRecommendation);
    const [redirected, setRedirected] = useState(false);

    const [bypassAssessments, { data }] = useBypassAssessmentsMutation({
        client: appClient,
        onError: captureException,
    });
    const [startCareRecommendation] = useStartCareRecommendationMutation({
        client: appClient,
    });
    const shouldTakeToPersonalEmailAddressFlowOutsideOnboarding =
        useShouldTakeToRequestPersonalEmailAddressFlowRequiredOnly();

    useEffect(() => {
        // In case someone is trying to access `authenticated` url without being authenticated
        if (!props.authenticated) {
            return navigationService.navigate('/');
        }

        dispatch.user.fetchUserInfo().then(user => {
            configureScope(scope => {
                scope.setUser({ id: `${user.id}` });
            });

            analyticsClient.identify(user.uuid, {
                company: user.company,
                company_uuid: user.companyUuid,
            });

            clevertap.onUserLogin.push({
                Site: {
                    Identity: user.uuid,
                    Email: user.email,
                    'MSG-push': true,
                },
            });

            const clevertapId = clevertap.getCleverTapID();
            if (clevertapId) {
                dispatch.user.updateUserInfo({ clevertapId });
            }

            if (!user?.languagePreferences?.length) {
                const langName =
                    Object.keys(languages).find(key => languages[key] === i18n.language) ||
                    config.defaultLanguageFullName;

                dispatch.user.updateUserInfo({ languagePreferences: [langName] });
            }

            dispatch.providers.fetchMyProviders();
        });

        dispatch.goalCategories.fetchGoalsCategories();
        dispatch.goalInterests.fetchGoalInterests();

        dispatch.assessments.fetchAssessments();
        dispatch.assessments.fetchLatestScores();

        dispatch.admin.fetchAdminInfo();
        dispatch.videoSessions.fetchSessions();
        dispatch.countries.fetchCountries();
        dispatch.languages.fetchLanguages();
    }, []);

    // this will check if there is a session to rate and navigate to rate a session flow if so
    // ONLY checks for invoiced sessions with `out of the app` OR `roster only` providers
    const checkIfShouldRateSession = async () => {
        try {
            const { data } = await appClient.query({
                query: GetLastInvoicedCoachingVisitDocument,
                fetchPolicy: 'network-only',
            });

            if (!data) {
                throw new Error('GetLastInvoicedCoachingVisitQuery: no response received');
            }

            const uuid = data?.lastInvoicedCoachingVisit?.uuid;

            if (uuid) {
                navigationService.navigate('/rate-session', { session: uuid });
            }
        } catch (error) {
            captureException(error);
        }
    };

    const navigateToTheRightPage = async () => {
        const nextPageSearch = localStorage.getItem('requestedUrlParams') || '';
        let nextPage = localStorage.getItem('requestedUrl') || '/dashboard';
        let nextPageState = queryString.parse(nextPageSearch);

        localStorage.removeItem('requestedUrl');
        localStorage.removeItem('requestedUrlParams');

        const navigateToNextPage = () => {
            const nextPageStateWithSearch = {
                ...nextPageState, // spread those params here, to maintain backward compatibility (some pages use searchParams from location.state)
                ...(nextPageSearch && { searchParams: nextPageSearch }), // pass search params here, to have access to it in Route (@modernhealth/web-analytics) and be able to separate it from rest of location.state properties
            };
            navigationService.navigate(nextPage, nextPageStateWithSearch);
        };

        if (props.user.role === 'MEMBER') {
            if (
                props.onboardingCompleted &&
                !props.sessionUuidForPreSessionAssessments &&
                !shouldTakeToPersonalEmailAddressFlowOutsideOnboarding
            ) {
                checkIfShouldRateSession();
            }

            if (props.sessionUuidForPreSessionAssessments) {
                nextPage = '/pre-session-assessments-intro';
                nextPageState = { sessionUuid: props.sessionUuidForPreSessionAssessments };
            }

            if (
                props.onboardingCompleted &&
                shouldTakeToPersonalEmailAddressFlowOutsideOnboarding
            ) {
                nextPage = '/preferred-email';
            }

            // bypassing the onboarding and letting user see the content
            if (
                !props.onboardingCompleted &&
                flags?.showContentWithoutOnboardingWeb &&
                urlsThatSkipsOnboarding.some(url => nextPage.includes(url))
            ) {
                let careRecUuid = careRecommendation?.uuid;

                // there will be no care recommendation if user didn't complete first part of the onboarding yet (topics/categories)
                // so we need to start a new one
                if (!careRecUuid) {
                    try {
                        const response = await startCareRecommendation();
                        const uuid =
                            response?.data?.startCareRecommendation?.careRecommendation?.uuid;
                        if (!uuid) {
                            throw new Error(
                                '[Bypass onboarding] Could not start new care recommendation',
                            );
                        }

                        careRecUuid = uuid;
                        await dispatch.user.fetchUserInfo();
                    } catch (error) {
                        captureException(error);
                    }
                }

                // this will set care recommendation's state to `BypassedAssessments` which informs us that user has skipped the onboarding
                if (careRecUuid) {
                    try {
                        const response = await bypassAssessments({
                            variables: { careRecommendationUuid: careRecUuid },
                        });

                        const newCareRecUuid =
                            response?.data?.bypassAssessments?.careRecommendation?.uuid;
                        if (!newCareRecUuid) {
                            throw new Error(
                                '[Bypass onboarding] Did not receive correct care recommendation in the response',
                            );
                        }

                        dispatch.user.markCareRecommendationAsBypassed(newCareRecUuid);
                        return navigateToNextPage();
                    } catch (error) {
                        withScope(scope => {
                            scope.setExtra('error_info', 'Error: failed to bypass onboarding');
                            scope.setExtra('response', data);
                            captureException(error);
                        });
                    }
                }
            }

            if (!props.onboardingSecondStepCompleted) {
                nextPage = '/onboarding-wellness';
            }

            if (!props.onboardingFirstStepCompleted) {
                nextPage = props.user.emailConfirmed
                    ? '/completion-page'
                    : props.user.newEmailAddressNotConfirmed
                    ? '/preferred-email-sent'
                    : isSSOFlowRegistrationSource(props.user.registrationSource)
                    ? '/registration'
                    : '/preferred-email';
            }
        }

        if (nextPage.includes('/dashboard') && props.user.phq9Access?.showTakeOverScreen) {
            nextPage = '/takeover-page';
        }

        navigateToNextPage();
    };

    useEffect(() => {
        if (
            !props.userCoachesFetched ||
            !props.userDataFetched ||
            !props.onboardingInfoFetched ||
            !props.coachingSessionsFetchCompleted ||
            !!redirected
        ) {
            return;
        }

        setRedirected(true);
        navigateToTheRightPage();
    }, [props, redirected]);

    return <CenteredSpinner />;
};

const mapStateToProps = (state: RootState) => ({
    authenticated: isAuthenticated(state),
    userDataFetched: isUserDataFetched(state),
    user: getUserInformation(state),
    onboardingInfoFetched: isOnboardedInfoFetched(state),
    onboardingCompleted: isOnboardingCompleted(state),
    onboardingFirstStepCompleted: isOnboardingFirstStepCompleted(state),
    onboardingSecondStepCompleted: isOnboardingSecondStepCompleted(state),
    userCoachesFetched: isUserProvidersFetchCompleted(state),
    coachingSessionsFetchCompleted: isSessionFetchingCompleted(state),
    sessionUuidForPreSessionAssessments: shouldShowPreSessionAssessments(state),
});

export default connect(mapStateToProps)(PostAuth);
